// fbx-loader.service.ts
import { Injectable } from '@angular/core';
import * as THREE from 'three';

/**
 * テスト的な実装。
 * 
 * fbxをパースするのにとても時間がかかり、
 * メインスレッドで行うとUIをブロックしてしまう。
 * なので、web workerにできないかと考えていた。
 * 
 * しかし、FBXLoaderがDOMに依存しているため、
 * web workerで動かそうとすると、
 * 
 * `window is not defined`
 * 
 * というエラーが出てしまう。
 * 
 * なので、この手法は使えない。
 * 
 * またFBXLoaderからDOM依存を取り除いて、web workerで使えるようにするのは、
 * 理論的には可能だが、
 * three.jsは今のところ行うつもりはないようだ。
 * 
 * @see https://github.com/mrdoob/three.js/issues/15556
 * 
 * 仕方がないので、fbxをサーバーサイドでglbに変える方針で考える。
 * 
 * @see https://github.com/facebookincubator/FBX2glTF
 */
@Injectable({ providedIn: 'root' })
export class FbxLoaderService {

  loadFbx(arrayBuffer: ArrayBuffer): Promise<THREE.Group | null> {
    return new Promise((resolve, reject) => {
      // Angular 13ではnew Worker()を直接使う
      if (typeof Worker !== 'undefined') {
        const worker = new Worker(new URL('./fbx.worker', import.meta.url));
        worker.onmessage = (event) => {
          if (event.data.status === 'error') {
            reject(event.data.message);
            worker.terminate();
            return;
          }
          if (event.data.status === "debug") {
            console.warn(event.data.message);
            return;
          }
          // メインスレッドでThree.jsオブジェクトを再構築
          const group = new THREE.Group();

          for (const geoData of event.data.geometries) {
            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute('position',
              new THREE.BufferAttribute(geoData.position, 3));
            if (geoData.normal) {
              geometry.setAttribute('normal',
                new THREE.BufferAttribute(geoData.normal, 3));
            }
            if (geoData.uv) {
              geometry.setAttribute('uv',
                new THREE.BufferAttribute(geoData.uv, 2));
            }
            if (geoData.index) {
              geometry.setIndex(new THREE.BufferAttribute(geoData.index, 1));
            }

            const mesh = new THREE.Mesh(
              geometry,
              new THREE.MeshStandardMaterial()
            );
            mesh.matrix.fromArray(geoData.matrixWorld);
            mesh.matrixAutoUpdate = false;
            group.add(mesh);
          }

          resolve(group);
          worker.terminate();
        };
        worker.onerror = (error) => {
          console.warn(error);
          reject(error);
          worker.terminate();
        };
        console.warn("send data to web worker:", worker, arrayBuffer)
        // ArrayBufferをTransferableとして渡す（コピーなし・高速）
        worker.postMessage({ arrayBuffer }, [arrayBuffer]);
      } else {
        // Web Workers are not supported in this environment.
        // You should add a fallback so that your program still executes correctly.
        console.warn("Web workers are not implemented in this browser.")
      }
    });
  }
}
